/// <reference types="vite/client" />

/**
 * 官网 AI 客服客户端。
 *
 * 官网是 Cloudflare Pages，AI 能力统一在 api.havorix.cn（CONSOLE Worker）。
 * 生产环境跨源调用，需要服务端 ALLOWED_ORIGINS 放行 havorix.cn；
 * 本地开发默认指向 wrangler dev（8790），可用 VITE_CONSOLE_API_BASE 覆盖。
 */

import { API_BASE } from './apiBase';

export interface AssistantStatus {
  surface: 'website';
  available: boolean;
  greeting: string;
  suggestedQuestions: string[];
}

export interface AssistantChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AssistantSource {
  id: string;
  title: string;
}

export class AssistantError extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null = null) {
    super(message);
    this.name = 'AssistantError';
    this.code = code;
  }
}

/** 官网单次对话允许的最大回答条数；达到后停止对话并引导联系页。 */
export const WEBSITE_REPLY_LIMIT = 10;

export async function fetchAssistantStatus(signal?: AbortSignal): Promise<AssistantStatus | null> {
  try {
    const response = await fetch(`${API_BASE}/api/assistant/status?surface=website`, { signal });
    if (!response.ok) return null;
    const body = (await response.json()) as { ok?: boolean; data?: AssistantStatus };
    if (body.ok !== true || !body.data) return null;
    return body.data;
  } catch {
    return null;
  }
}

export interface StreamHandlers {
  onSources?: (sources: AssistantSource[]) => void;
  onDelta: (text: string) => void;
  onDone?: () => void;
}

async function readError(response: Response): Promise<{ message: string; code: string | null }> {
  try {
    const body = (await response.json()) as {
      error?: { message?: unknown; code?: unknown };
    };
    const code = typeof body.error?.code === 'string' ? body.error.code : null;
    if (typeof body.error?.message === 'string' && body.error.message !== '') {
      return { message: body.error.message, code };
    }
  } catch {
    // 出错时上游可能返回非 JSON，统一走下面的兜底文案。
  }
  if (response.status === 429) return { message: '提问过于频繁，请稍后再试', code: null };
  if (response.status === 403) return { message: 'AI 客服暂未开放', code: null };
  return { message: 'AI 客服暂时不可用，请稍后再试', code: null };
}

/** 协议：sources → delta... → done；错误时为 error。 */
export async function streamAssistantChat(
  messages: AssistantChatMessage[],
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE}/api/assistant/chat`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ surface: 'website', messages }),
    signal,
  });

  if (!response.ok || !response.body) {
    const failure = await readError(response);
    throw new AssistantError(failure.message, failure.code);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary >= 0) {
        const chunk = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        handleChunk(chunk, handlers);
        boundary = buffer.indexOf('\n\n');
      }
    }
    if (buffer.trim() !== '') handleChunk(buffer, handlers);
  } finally {
    reader.releaseLock();
  }
}

function handleChunk(chunk: string, handlers: StreamHandlers): void {
  for (const line of chunk.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) continue;
    const raw = trimmed.slice(5).trim();
    if (raw === '' || raw === '[DONE]') continue;

    let payload: unknown;
    try {
      payload = JSON.parse(raw);
    } catch {
      continue;
    }
    if (payload === null || typeof payload !== 'object') continue;
    const event = payload as Record<string, unknown>;

    if (event.type === 'delta' && typeof event.text === 'string') {
      handlers.onDelta(event.text);
    } else if (event.type === 'sources' && Array.isArray(event.sources)) {
      handlers.onSources?.(
        event.sources
          .filter((item): item is Record<string, unknown> => item !== null && typeof item === 'object')
          .map((item) => ({
            id: typeof item.id === 'string' ? item.id : '',
            title: typeof item.title === 'string' ? item.title : '',
          })),
      );
    } else if (event.type === 'done') {
      handlers.onDone?.();
    } else if (event.type === 'error') {
      throw new AssistantError(
        typeof event.message === 'string' ? event.message : '生成回答时发生错误',
      );
    }
  }
}
